import { FileText, ImageOff, type LucideIcon, Video } from 'lucide-react';

import type { BrandAssetType } from './types';

/** The icon an asset without a preview shows, by its media kind. */
export const MEDIA_KIND_ICON: Record<MediaKind, LucideIcon> = { video: Video, document: FileText, image: ImageOff };

export const LOGO_MAX_BYTES = 5 * 1024 * 1024;
export const VISUAL_MAX_BYTES = 200 * 1024 * 1024;
export const AUDIO_MAX_BYTES = 100 * 1024 * 1024;
export const AUDIO_ACCEPT = 'audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,.mp3,.wav,.m4a';
export const FILE_MAX_BYTES = 50 * 1024 * 1024;
export const FILE_ACCEPT = 'application/pdf,.pdf';

/** The folder of each asset type, below `{org}/brand-hub/{slug}/`. */
export const ASSET_FOLDERS = {
  logo: 'logos',
  visual_element: 'visual-elements',
  audio: 'audio',
  file: 'files',
  brand_guideline: 'brand-guidelines',
} as const satisfies Record<BrandAssetType, string>;

export type BrandFolder = (typeof ASSET_FOLDERS)[BrandAssetType] | 'fonts';

/** One brand's folder, or one of the organization-wide ones. */
export type UploadTarget =
  | { brandSlug: string; folder: BrandFolder }
  | { folder: 'templates' | 'templates/media' | 'products/uploads' | 'products/imports' };

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm', 'm4v', 'mkv', 'avi'];

export type MediaKind = 'image' | 'video' | 'document';

/** Returns the last path segment of an object name. */
export function fileNameOf(objectName: string): string {
  return objectName.split('/').pop() ?? objectName;
}

function extensionOf(fileName: string): string {
  const extension = fileName.split('.').pop();
  return extension && extension !== fileName ? extension.toLowerCase() : 'bin';
}

/** Returns the media kind of an object name, read from its extension. */
export function mediaKindOf(objectName: string): MediaKind {
  const extension = extensionOf(objectName);
  if (extension === 'pdf') return 'document';
  return VIDEO_EXTENSIONS.includes(extension) ? 'video' : 'image';
}

/** Returns a new object name with the file extension, under the brand hub prefix of the organization. */
export function brandHubObjectName(
  organizationId: string,
  target: UploadTarget,
  fileName: string,
): string {
  const folder = 'brandSlug' in target ? `${target.brandSlug}/${target.folder}` : target.folder;
  return `${organizationId}/brand-hub/${folder}/${crypto.randomUUID()}.${extensionOf(fileName)}`;
}
